import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import UserForm from '../components/UserForm';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface User {
  id: number;
  name: string;
  username: string;
  email: string;
  phone_number: string;
  address: string;
  group_id: number;
  company_id: number;
  user_type: string;
  lang_id: number;
  published: number;
  group?: {
    id: number;
    title: string;
  };
}

interface Group {
  id: number;
  title: string;
}

interface Module {
  id: number;
  title: string;
  route: string;
}

interface Permission {
  module_id: number;
  module_name: string;
  route: string;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_print: boolean;
  can_export: boolean;
  source?: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface UsersResponse {
  items: User[];
  pagination: PaginationInfo;
}

interface GroupsResponse {
  items: Group[];
  pagination: PaginationInfo;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: page.toString(),
        per_page: '15',
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiService.get<UsersResponse>('users', params);
      if (response.success) {
        setUsers(response.data.items);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiService.get<GroupsResponse>('groups');
      if (response.success) {
        setGroups(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUsers(currentPage, search);
      fetchGroups();
    }
  }, [token, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers(1, search);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleExport = async () => {
    if (!hasPermission('users', 'export')) {
      toast.error('You do not have permission to export users.');
      return;
    }

    try {
      const params: Record<string, any> = {};
      if (search) {
        params.search = search;
      }

      await apiService.downloadFile(`users/export?${new URLSearchParams(params)}`, `users-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Error exporting users:', error);
      toast.error('Error exporting users. Please try again.');
    }
  };

  const handlePrint = () => {
    if (!hasPermission('users', 'print')) {
      toast.error('You do not have permission to print users.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const tableElement = document.querySelector('.users-table');
      if (tableElement) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Users Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; }
                .header { text-align: center; margin-bottom: 20px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Users Report</h1>
                <p>Generated on: ${new Date().toLocaleDateString()}</p>
              </div>
              ${tableElement.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleAddUser = () => {
    if (!hasPermission('users', 'add')) {
      toast.error('You do not have permission to add users.');
      return;
    }
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (userId: number) => {
    if (!hasPermission('users', 'edit')) {
      toast.error('You do not have permission to edit users.');
      return;
    }
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
      setShowForm(true);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!hasPermission('users', 'delete')) {
      toast.error('You do not have permission to delete users.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await apiService.delete(`users/${userId}`);
        if (response.success) {
          fetchUsers(currentPage, search);
          toast.success('User deleted successfully');
        } else {
          toast.error(`Error deleting user: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Error deleting user. Please try again.');
      }
    }
  };

  const handleSubmitUser = async (userData: Omit<User, 'id'> & { id?: number }) => {
    setFormLoading(true);
    try {
      let response;
      if (editingUser) {
        response = await apiService.put(`users/${editingUser.id}`, userData);
      } else {
        response = await apiService.post('users', userData);
      }

      if (response.success) {
        setShowForm(false);
        setEditingUser(null);
        fetchUsers(currentPage, search);
        toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
      } else {
        toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Error saving user. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handlePermissionsClick = (user: User) => {
    navigate(`/permissions/user/${user.id}`, { 
      state: { 
        user,
        type: 'user'
      } 
    });
  };

  const getGroupName = (groupId: number) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.title : 'Unknown Group';
  };

  const renderActions = (user: User) => (
    <div className="flex space-x-2">
      {hasPermission('users', 'edit') && (
        <Button
          size="sm"
          onClick={() => handleEditUser(user.id)}
          className="bg-blue-500 hover:bg-blue-600"
        >
          Edit
        </Button>
      )}
      {hasPermission('users', 'delete') && (
        <Button
          size="sm"
          onClick={() => handleDeleteUser(user.id)}
          className="bg-red-500 hover:bg-red-600"
        >
          Delete
        </Button>
      )}
      <Button
        size="sm"
        onClick={() => handlePermissionsClick(user)}
        className="bg-green-500 hover:bg-green-600"
      >
        Permissions
      </Button>
    </div>
  );

  const data = users.map(user => ({
    ...user,
    group: getGroupName(user.group_id),
    actions: renderActions(user)
  }));

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            User Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage users, permissions, and access control
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                Search
              </Button>
            </form>
            <div className="flex gap-2">
              {hasPermission('users', 'print') && (
                <Button onClick={handlePrint} disabled={loading}>
                  Print
                </Button>
              )}
              {hasPermission('users', 'export') && (
                <Button onClick={handleExport} disabled={loading}>
                  Export CSV
                </Button>
              )}
              {hasPermission('users', 'add') && (
                <Button onClick={handleAddUser} disabled={loading}>
                  Add User
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone_number}</TableCell>
                    <TableCell>{user.user_type}</TableCell>
                    <TableCell>{getGroupName(user.group_id)}</TableCell>
                    <TableCell>
                      {renderActions(user)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {pagination.from} to {pagination.to} of {pagination.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <Button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* User Form Modal */}
        {showForm && (
          <UserForm
            user={editingUser}
            groups={groups}
            onSubmit={handleSubmitUser}
            onCancel={handleCancelForm}
            loading={formLoading}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Users; 